import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FormControl,
    IconButton,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
} from '@mui/material';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { useHealthPlanContext } from '../../hooks/healthPlanContext';

interface HealthPlanMultiSelectProps {
    value: string[] | string;
    onChange: (value: string[]) => void;
    label?: string;
    name?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    menuProps?: any;
}

const HealthPlanMultiSelect = ({
    value,
    onChange,
    label = 'Health Plan',
    name = 'Health Plan',
    size = 'small',
    fullWidth = true,
    menuProps,
}: HealthPlanMultiSelectProps) => {
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const { healthPlans } = useHealthPlanContext();

    const [open, setOpen] = useState(false);
    const [tempHealthPlan, setTempHealthPlan] = useState<string[]>([]);
    const hasInitializedHealthPlans = useRef(false);

    const filteredHealthPlans = useMemo(() => {
        const populationHealthPlanNames = population
            .map((p) => p.HealthPlan?.Name)
            .filter(Boolean);

        return healthPlans
            .filter((item) => populationHealthPlanNames.includes(item.Name))
            .sort((a, b) => a.Name.localeCompare(b.Name));
    }, [healthPlans, population]);

    const allHealthPlanIds = useMemo(() => {
        return filteredHealthPlans.map((p) => p.Id);
    }, [filteredHealthPlans]);

    const areSameHealthPlans = (first: string[], second: string[]) => {
        return (
            first.length === second.length &&
            first.every((id) => second.includes(id))
        );
    };

    const normalizeHealthPlanValue = useCallback(
        (healthPlanValue: any): string[] => {
            if (allHealthPlanIds.length === 0) {
                return [];
            }

            const values = Array.isArray(healthPlanValue)
                ? healthPlanValue
                : typeof healthPlanValue === 'string'
                    ? healthPlanValue.split(',')
                    : [];

            const cleanedValues = values
                .map((x) => x?.trim())
                .filter((x) => x);

            if (cleanedValues.length === 0 || cleanedValues.includes('All')) {
                return allHealthPlanIds;
            }

            const validIds = cleanedValues.filter((id) =>
                allHealthPlanIds.includes(id)
            );

            return validIds.length > 0 ? validIds : allHealthPlanIds;
        },
        [allHealthPlanIds]
    );

    useEffect(() => {
        if (!hasInitializedHealthPlans.current && allHealthPlanIds.length > 0) {
            hasInitializedHealthPlans.current = true;

            const initialValue = normalizeHealthPlanValue(value);

            setTempHealthPlan(initialValue);

            const currentValue = Array.isArray(value) ? value : [];

            if (!areSameHealthPlans(currentValue, initialValue)) {
                onChange(initialValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allHealthPlanIds.length, normalizeHealthPlanValue]);

    const selectedHealthPlans = useMemo(() => {
        return normalizeHealthPlanValue(
            tempHealthPlan.length > 0 ? tempHealthPlan : value
        );
    }, [tempHealthPlan, value, normalizeHealthPlanValue]);

    const isAllHealthPlansSelected =
        allHealthPlanIds.length > 0 &&
        selectedHealthPlans.length === allHealthPlanIds.length &&
        allHealthPlanIds.every((id) => selectedHealthPlans.includes(id));

    const commitHealthPlanChange = () => {
        const finalValue = normalizeHealthPlanValue(tempHealthPlan);

        setTempHealthPlan(finalValue);

        const currentValue = normalizeHealthPlanValue(value);

        if (!areSameHealthPlans(currentValue, finalValue)) {
            onChange(finalValue);
        }
    };

    const handleHealthPlanOpen = () => {
        const currentValue = normalizeHealthPlanValue(value);

        setTempHealthPlan(currentValue);
        setOpen(true);
    };

    const handleHealthPlanClose = () => {
        commitHealthPlanChange();
        setOpen(false);
    };

    const handleCloseIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        commitHealthPlanChange();
        setOpen(false);
    };

    return (
        <FormControl size={size} fullWidth={fullWidth}>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                open={open}
                value={selectedHealthPlans}
                label={label}
                name={name}
                onOpen={handleHealthPlanOpen}
                onClose={handleHealthPlanClose}
                onChange={(e) => {
                    const selectedValues =
                        typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value;

                    if (selectedValues.includes('All')) {
                        setTempHealthPlan(allHealthPlanIds);
                        return;
                    }

                    if (selectedValues.length === 0) {
                        setTempHealthPlan(allHealthPlanIds);
                        return;
                    }

                    const validIds = selectedValues.filter((id) =>
                        allHealthPlanIds.includes(id)
                    );

                    setTempHealthPlan(
                        validIds.length > 0 ? validIds : allHealthPlanIds
                    );
                }}
                renderValue={(selected) => {
                    const selectedValues = selected as string[];

                    const isAllSelected =
                        allHealthPlanIds.length > 0 &&
                        selectedValues.length === allHealthPlanIds.length &&
                        allHealthPlanIds.every((id) =>
                            selectedValues.includes(id)
                        );

                    if (isAllSelected) {
                        return 'All';
                    }

                    return filteredHealthPlans
                        .filter((plan) => selectedValues.includes(plan.Id))
                        .map((plan) => plan.Name)
                        .join(', ');
                }}
                MenuProps={{
                    ...menuProps,
                    PaperProps: {
                        ...menuProps?.PaperProps,
                        sx: {
                            ...menuProps?.PaperProps?.sx,
                            '&::-webkit-scrollbar': {
                                display: 'none',
                            },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '& .MuiList-root': {
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            },
                        },
                    },
                }}
            >
                <MenuItem
                    value="All"
                    sx={{
                        position: 'relative',
                        pr: 5,
                    }}
                >
                    <ListItemIcon>
                        {isAllHealthPlansSelected && (
                            <span style={{ fontSize: '14px' }}>✔</span>
                        )}
                    </ListItemIcon>

                    <ListItemText primary="All" />

                    <IconButton
                        size="small"
                        onClick={handleCloseIconClick}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        sx={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                        }}
                    >
                        <FileDownloadDoneIcon fontSize="small" />
                    </IconButton>
                </MenuItem>

                {filteredHealthPlans.map((plan) => (
                    <MenuItem key={plan.Id} value={plan.Id}>
                        <ListItemIcon>
                            {selectedHealthPlans.includes(plan.Id) && (
                                <span style={{ fontSize: '14px' }}>✔</span>
                            )}
                        </ListItemIcon>

                        <ListItemText primary={plan.Name} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default HealthPlanMultiSelect;